import Tooltip from 'components/@extended/Tooltip';
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from 'components/icons';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import { useCallback } from 'react';
import { Control, Controller, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';
import { useSelector } from 'store';
import { FeatureFlag } from 'types/features';
import { InvoiceMatterForm } from 'types/invoice-details';
import { cn } from 'utils/cn';
import { indexedFieldName } from 'utils/formPath';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil.ts';

type Props = {
    isRowDisabled: boolean;
    readonly: boolean;
    control: Control<InvoiceMatterForm>;
    index: number;
    onSubmit: () => void;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    getValues: UseFormGetValues<InvoiceMatterForm>;
};

export const AdvancedBillingDisplay = ({
    control,
    index,
    isRowDisabled,
    readonly,
    onSubmit,
    setValue,
    getValues,
}: Props) => {
    const { buttonsDisabled: areButtonsDisabled } = useSelector((state) => state.buttons);

    const handleOnVisibilityIconClick = useCallback(() => {
        const fieldName = indexedFieldName('invoiceExpenses', index, 'isHidden');
        const isHidden = getValues(fieldName);
        setValue(fieldName, !isHidden);
        onSubmit();
    }, [getValues, index, onSubmit, setValue]);

    const [isHidden] = useWatch({ control, name: [indexedFieldName('invoiceExpenses', index, 'isHidden')] });

    const hasPermissionToEnableMirroring = useHasPermissions(['UPDATE.INVOICES.TC_EXP_MIRROR']);

    return (
        <div className="opacity-decreasing-when-disabled flex flex-col">
            <div className="flex flex-row flex-wrap gap-2 py-2">
                <CheckboxInput
                    control={control}
                    label={'invoices.details.table.mirrored'}
                    name={indexedFieldName('invoiceExpenses', index, 'isMirrored')}
                    onChange={onSubmit}
                    disabled={!hasPermissionToEnableMirroring || areButtonsDisabled}
                    readonly={readonly}
                />
                <FeatureFlagged action="show" flag={FeatureFlag.HIDE_TIMECARD_EXPENSE_FROM_INVOICE}>
                    <Controller
                        control={control}
                        name={indexedFieldName('invoiceExpenses', index, 'isHidden')}
                        render={({ field }) => (
                            <Tooltip text={`invoices.details.table.${isHidden ? 'unhide' : 'hide'}`}>
                                <div
                                    className={cn(
                                        'flex cursor-pointer items-center justify-center rounded-md p-1.5',
                                        field.value && !areButtonsDisabled ? 'bg-secondary-light' : 'bg-grey-100',
                                        (isRowDisabled || readonly || !hasPermissionToEnableMirroring) &&
                                            'pointer-events-none'
                                    )}
                                    role="button"
                                    tabIndex={areButtonsDisabled ? -1 : 0}
                                    aria-label={t(`invoices.details.table.${isHidden ? 'unhide' : 'hide'}`)}
                                    onClick={areButtonsDisabled ? undefined : handleOnVisibilityIconClick}
                                    onKeyDown={(event) => {
                                        if (areButtonsDisabled) return;
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            handleOnVisibilityIconClick();
                                        }
                                    }}
                                >
                                    <span
                                        className="t-icon-swap"
                                        data-state={field.value ? 'b' : 'a'}
                                        aria-hidden="true"
                                    >
                                        <span className="t-icon" data-icon="a">
                                            <VisibilityIcon fontSize="small" />
                                        </span>
                                        <span className="t-icon" data-icon="b">
                                            <VisibilityOffIcon fontSize="small" />
                                        </span>
                                    </span>
                                </div>
                            </Tooltip>
                        )}
                    />
                </FeatureFlagged>
            </div>
        </div>
    );
};
